import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { PropertyStatus } from "@/features/properties/types";
import { FoundingStatusBadge } from "./FoundingStatusBadge";
import { AssignFoundingDialog } from "./AssignFoundingDialog";
import { UnassignFoundingDialog } from "./UnassignFoundingDialog";
import { useOwnerFoundingProperties } from "./use-owner-founding-properties";
import { OWNER_PROPERTIES_PAGE_SIZE } from "./constants";
import { currentPercent, deriveFoundingStatus, type OwnerPropertyRow } from "./types";

interface OwnerFoundingPropertiesPanelProps {
  readonly userId: string;
}

const STATUS_OPTIONS = ["all", ...Object.values(PropertyStatus)] as const;

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top font-sans text-sm";

/**
 * Per-host founding assignment surface (ADR-0010 §9). Search + status filter
 * over the owner's properties (founders and non-founders), a Gmail-style
 * multi-select table, and a bulk bar that opens the shared assign/unassign
 * dialogs. Selection resets on every filter/page change so a stale row — one
 * no longer on screen — can never be assigned/unassigned. Checkboxes and the
 * bulk bar only render for ADMIN; FINANCE is read-only.
 */
export function OwnerFoundingPropertiesPanel({ userId }: OwnerFoundingPropertiesPanelProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canMutate = currentUser?.role === Role.WANDERFIT_ADMIN;
  const canRead = canMutate || currentUser?.role === Role.WANDERFIT_FINANCE;

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);

  const debouncedQuery = useDebouncedValue(query, 300);

  const { data, isLoading, isFetching, isError } = useOwnerFoundingProperties({
    userId,
    query: debouncedQuery,
    status,
    page,
    enabled: canRead,
  });

  const rows = useMemo<readonly OwnerPropertyRow[]>(() => data?.rows ?? [], [data]);
  const total = data?.total ?? 0;
  const limit = data?.limit ?? OWNER_PROPERTIES_PAGE_SIZE;
  const selectedIds = [...selected];

  // Reset the selection whenever the visible result set can change under us —
  // the same staleness guard the cockpit uses. A billing-affecting bulk action
  // must only ever apply to rows currently on screen.
  useEffect(() => {
    setSelected(new Set());
  }, [debouncedQuery, status, page]);

  // A filter change must send us back to page 1 — otherwise a narrower filter
  // could land on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, status]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (checked: boolean) =>
    setSelected(checked ? new Set(rows.map((r) => r.propertyId)) : new Set());
  const clear = () => setSelected(new Set());

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.propertyId));
  const hasPrev = page > 1;
  const hasNext = page * limit < total;

  return (
    <div className="space-y-4" data-testid="owner-founding-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <label
            htmlFor="owner-founding-search"
            className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            {t("founding.ownerProperties.search")}
          </label>
          <Input
            id="owner-founding-search"
            data-testid="owner-founding-search"
            value={query}
            placeholder={t("founding.ownerProperties.searchPlaceholder")}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="owner-founding-status"
            className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            {t("founding.ownerProperties.status")}
          </label>
          <select
            id="owner-founding-status"
            data-testid="owner-founding-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 font-sans text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === "all"
                  ? t("founding.ownerProperties.statusAll")
                  : t(`properties.statusLabel.${s}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {canMutate && selected.size > 0 && (
        <div
          data-testid="owner-founding-bulk-bar"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
        >
          <span className="font-sans text-sm">
            {t("founding.bulk.selected", { count: selected.size })}
          </span>
          <Button
            type="button"
            size="sm"
            data-testid="owner-founding-bulk-assign"
            onClick={() => setAssignOpen(true)}
          >
            {t("founding.bulk.assign")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="owner-founding-bulk-unassign"
            onClick={() => setUnassignOpen(true)}
          >
            {t("founding.bulk.unassign")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="owner-founding-bulk-clear"
            onClick={clear}
          >
            {t("founding.bulk.clear")}
          </Button>
        </div>
      )}

      {isError ? (
        <p data-testid="owner-founding-error" className="font-sans text-sm text-destructive">
          {t("founding.ownerProperties.loadError")}
        </p>
      ) : isLoading ? (
        <p data-testid="owner-founding-loading" className="font-sans text-sm text-muted-foreground">
          {t("founding.loading")}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="owner-founding-empty" className="font-sans text-sm text-muted-foreground">
          {t("founding.ownerProperties.empty")}
        </p>
      ) : (
        <div
          aria-busy={isFetching}
          className={
            isFetching
              ? "overflow-x-auto rounded-lg border border-border opacity-70 transition-opacity"
              : "overflow-x-auto rounded-lg border border-border"
          }
        >
          <table data-testid="owner-founding-table" className="w-full border-collapse">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                {canMutate && (
                  <th className={TH_CLASS}>
                    <input
                      type="checkbox"
                      aria-label={t("founding.table.selectAll")}
                      data-testid="owner-founding-select-all"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                )}
                <th className={TH_CLASS}>{t("founding.table.property")}</th>
                <th className={TH_CLASS}>{t("founding.table.status")}</th>
                <th className={TH_CLASS}>{t("founding.table.discount")}</th>
                <th className={TH_CLASS}>{t("founding.table.tier")}</th>
                <th className={TH_CLASS}>{t("founding.ownerProperties.propertyStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = r.founding !== null ? currentPercent(r.founding) : null;
                return (
                  <tr
                    key={r.propertyId}
                    data-testid={`owner-founding-row-${r.propertyId}`}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    {canMutate && (
                      <td className={TD_CLASS}>
                        <input
                          type="checkbox"
                          aria-label={t("founding.table.selectRow", {
                            name: r.propertyName,
                          })}
                          data-testid={`owner-founding-select-${r.propertyId}`}
                          checked={selected.has(r.propertyId)}
                          onChange={() => toggle(r.propertyId)}
                        />
                      </td>
                    )}
                    <td className={TD_CLASS}>
                      <p className="font-medium">{r.propertyName}</p>
                    </td>
                    <td className={TD_CLASS}>
                      {r.founding !== null ? (
                        <FoundingStatusBadge
                          status={deriveFoundingStatus(r.founding)}
                          testId={`owner-founding-status-${r.propertyId}`}
                        />
                      ) : (
                        <span
                          data-testid={`owner-founding-status-${r.propertyId}`}
                          className="font-sans text-xs text-muted-foreground"
                        >
                          {t("founding.ownerProperties.notEnrolled")}
                        </span>
                      )}
                    </td>
                    <td className={TD_CLASS}>{pct === null ? "—" : `${pct}%`}</td>
                    <td className={TD_CLASS}>{r.currentTier ?? "—"}</td>
                    <td className={TD_CLASS}>{r.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="owner-founding-prev"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t("founding.ownerProperties.prev")}
          </Button>
          <span className="font-sans text-sm text-muted-foreground">
            {t("founding.ownerProperties.pageOf", {
              page,
              total: Math.max(1, Math.ceil(total / limit)),
            })}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="owner-founding-next"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("founding.ownerProperties.next")}
          </Button>
        </div>
      )}

      {canMutate && (
        <>
          <AssignFoundingDialog
            propertyIds={selectedIds}
            open={assignOpen}
            onOpenChange={setAssignOpen}
            onDone={clear}
          />
          <UnassignFoundingDialog
            propertyIds={selectedIds}
            open={unassignOpen}
            onOpenChange={setUnassignOpen}
            onDone={clear}
          />
        </>
      )}
    </div>
  );
}
